// src/pages/Orders/Orders.jsx

import React, { useEffect, useMemo, useState } from 'react';
import MainLayout from '../../components/Layout/MainLayout';
import {
  Search,
  Filter,
  Eye,
  Download,
  X,
  Package,
  Truck,
  CheckCircle,
  Clock,
} from 'lucide-react';
import './Orders.css';
import Cookies from 'js-cookie';
import { handleGetApi, handlePostApi } from '../../api/api';

const Orders = () => {
  const token = JSON.parse(Cookies.get('auth_token'));
    const headers = {
      Authorization: token
    }
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);

  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // =========================================================
  // FETCH ORDERS
  // =========================================================
  const fetchOrders = async () => {
    try {
      setLoading(true);

      const res = await handleGetApi('admin/get/orders', headers);
      if (!res.success) {
        throw new Error(res.message || 'Failed to fetch orders');
      }
      setAllOrders(res.data || []);
      setOrders(res.data || []);
    } catch (error) {
      console.error('Fetch Orders Error:', error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // =========================================================
  // SEARCH + FILTER
  // =========================================================
  const filteredOrders = useMemo(() => {
    let filtered = [...allOrders];

    if (search.trim()) {
      const q = search.toLowerCase();

      filtered = filtered.filter((order) => {
        return (
          order.order_number?.toLowerCase().includes(q) ||
          order.customer_name?.toLowerCase().includes(q) ||
          order.email?.toLowerCase().includes(q) ||
          order.phone?.toLowerCase().includes(q)
        );
      });
    }

    if (statusFilter) {
      filtered = filtered.filter(
        (order) => order.order_status === statusFilter
      );
    }

    return filtered;
  }, [allOrders, search, statusFilter]);

  useEffect(() => {
    setOrders(filteredOrders);
  }, [filteredOrders]);

  // =========================================================
  // OPEN DETAILS
  // =========================================================
  const handleOpenDetails = async (id) => {
    try{
      const res = await handleGetApi(`admin/get/order/${id}`, headers);
      console.log(res)
      if(res.success){
        setSelectedOrder(res.data);
    setIsDetailModalOpen(true);
      }
    } catch(err){
      console.log(err)
    }
  };

  // =========================================================
  // UPDATE ORDER STATUS
  // =========================================================
  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      setStatusUpdating(true);
      const sendData = {
            order_status: newStatus,
      }
      const res = await handlePostApi(`admin/update/order-status/${orderId}`, sendData, headers)
      console.log(res)
      if (!res.success) {
        throw new Error(res.message || 'Failed to update status');
      }

      // Update list
      const updatedOrders = allOrders.map((order) =>
        order.id === orderId
          ? { ...order, order_status: newStatus }
          : order
      );

      setAllOrders(updatedOrders);

      // Update selected order
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({
          ...selectedOrder,
          order_status: newStatus,
        });
      }
    } catch (error) {
      console.error('Update Status Error:', error);
      alert(error.message);
    } finally {
      setStatusUpdating(false);
    }
  };

  // =========================================================
  // STATUS ICON
  // =========================================================
  const getStatusIcon = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'delivered':
        return <CheckCircle size={14} />;
      case 'shipped':
        return <Truck size={14} />;
      case 'processing':
        return <Clock size={14} />;
      default:
        return <Package size={14} />;
    }
  };

  // =========================================================
  // FORMATTERS
  // =========================================================
  const formatCurrency = (amount) => {
    return `₹${Number(amount || 0).toLocaleString('en-IN')}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';

    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getCustomerName = (order) => {
    if (order.customer_name) return order.customer_name;

    const first = order.first_name || '';
    const last = order.last_name || '';

    return `${first} ${last}`.trim() || 'N/A';
  };

  const getShippingAddress = (order) => {
    const parts = [
      order.address,
      order.apartment,
      order.city,
      order.postal_code,
    ].filter(Boolean);

    return parts.join(', ') || 'N/A';
  };

  // =========================================================
  // EXPORT CSV
  // =========================================================
  const handleExportCSV = () => {
    const headers = [
      'Order Number',
      'Customer',
      'Email',
      'Phone',
      'Date',
      'Total',
      'Order Status',
      'Payment Status',
    ];

    const rows = orders.map((order) => [
      order.order_number,
      getCustomerName(order),
      order.email || '',
      order.phone || '',
      formatDate(order.created_at),
      order.total_amount,
      order.order_status,
      order.payment_status,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((field) => `"${String(field ?? '').replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', 'orders.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // =========================================================
  // PRINT INVOICE
  // =========================================================
  const handlePrintInvoice = () => {
    window.print();
  };

  return (
    <MainLayout title="Order Management">
      <div className="orders-container fade-in">
        {/* HEADER */}
        <div className="orders-header">
          <div className="search-filters">
            <div className="search-input">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search by Order ID or Customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="filter-btn">
              <Filter size={18} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <button className="export-btn" onClick={handleExportCSV}>
            <Download size={20} />
            <span>Export CSV</span>
          </button>
        </div>

        {/* TABLE */}
        <div className="orders-card">
          <div className="table-responsive">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Total Amount</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center' }}>
                      Loading orders...
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center' }}>
                      No orders found.
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr key={order.id}>
                      <td className="order-id-cell">
                        {order.order_number}
                      </td>

                      <td>
                        <div className="customer-info">
                          <span className="customer-name">
                            {getCustomerName(order)}
                          </span>
                          <span className="customer-email">
                            {order.email}
                          </span>
                        </div>
                      </td>

                      <td>{formatDate(order.created_at)}</td>

                      <td className="order-total">
                        {formatCurrency(order.total_amount)}
                      </td>

                      <td>
                        <span
                          className={`status-pill ${
                            order.payment_status || ''
                          }`}
                        >
                          {order.payment_status || 'pending'}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`status-pill ${
                            order.order_status || ''
                          }`}
                        >
                          {getStatusIcon(order.order_status)}
                          {order.order_status}
                        </span>
                      </td>

                      <td>
                        <div className="action-btns">
                          <button
                          style={{color:"#fff", backgroundColor: '#b98f36', width: "auto", height:"auto"}}
                            className="view-btn"
                            title="View Details"
                            onClick={() =>
                              handleOpenDetails(order?.id)
                            }
                          >
                            <Eye size={18} />
                          </button>

                          <select
                            className="status-select"
                            style={{backgroundColor:"black"}}
                            value={order.order_status}
                            disabled={statusUpdating}
                            onChange={(e) =>
                              handleUpdateStatus(
                                order.id,
                                e.target.value
                              )
                            }
                          >
                           <option value="pending">
    Pending
  </option>

  <option value="confirmed">
    Confirmed
  </option>

  <option value="processing">
    Processing
  </option>

  <option value="packed">
    Packed
  </option>

  <option value="shipped">
    Shipped
  </option>

  <option value="delivered">
    Delivered
  </option>

  <option value="cancelled">
    Cancelled
  </option>

  <option value="returned">
    Returned
  </option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ORDER DETAIL MODAL */}
      {isDetailModalOpen && selectedOrder?.order && (
        <div className="modal-overlay">
          <div className="modal-content detail-modal fade-in">
            <div className="modal-header">
              <div className="header-title">
                <h2>Order Details</h2>
                <span className="order-id-tag">
                  {selectedOrder?.order?.order_number}
                </span>
              </div>

              <button
                className="close-modal"
                onClick={() => setIsDetailModalOpen(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="order-detail-grid">
              {/* Customer Info */}
              <div className="detail-section">
                <h3>Customer Information</h3>
                <div className="info-box">
                  <p>
                    <strong>Name:</strong>{' '}
                    {getCustomerName(selectedOrder?.order)}
                  </p>
                  <p>
                    <strong>Email:</strong>{' '}
                    {selectedOrder?.order?.email || 'N/A'}
                  </p>
                  <p>
                    <strong>Phone:</strong>{' '}
                    {selectedOrder?.order?.phone || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="detail-section">
                <h3>Shipping Address</h3>
                <div className="info-box">
                  <p>{getShippingAddress(selectedOrder?.order)}</p>
                </div>
              </div>

              {/* Order Items */}
              <div className="detail-section full-width">
                <h3>Order Items</h3>

                <div className="items-table-wrapper">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Price</th>
                        <th>Qty</th>
                        <th>Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(selectedOrder.items || []).map(
                        (item, index) => (
                          <tr key={index}>
                            <td>{item.product_name}</td>
                            <td>
                              {formatCurrency(
                                item.unit_price
                              )}
                            </td>
                            <td>x{item.quantity}</td>
                            <td>
                              {formatCurrency(
                                item.line_total
                              )}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>

                    <tfoot>
                      <tr>
                        <td colSpan="3" className="text-right">
                          Subtotal
                        </td>
                        <td>
                          {formatCurrency(
                            selectedOrder?.order?.subtotal
                          )}
                        </td>
                      </tr>

                      {/* <tr>
                        <td colSpan="3" className="text-right">
                          Shipping
                        </td>
                        <td>
                          {formatCurrency(
                            selectedOrder?.order?.shipping_charge
                          )}
                        </td>
                      </tr> */}

                      <tr className="grand-total">
                        <td colSpan="3" className="text-right">
                          Grand Total
                        </td>
                        <td>
                          {formatCurrency(
                            selectedOrder?.order?.total_amount
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              {/* <button
                className="print-btn"
                onClick={handlePrintInvoice}
              >
                <Download size={18} />
                Print Invoice
              </button> */}

              <button
                className="save-btn"
                onClick={() => setIsDetailModalOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Orders;